import ArticlePrice from "@/pages/(app)/returns/view/components/return-articles/article-price";
import { ReturnRequestExchangeNewProductForExchangeCreditDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";

type NewExchangeArticleProps = {
  article: ReturnRequestExchangeNewProductForExchangeCreditDto;
  currency: string;
};

// BBS-363: the price comes from the customer's cart. `catalogPrice` is the
// server's reference, read when the return was created; lower is often a
// cart discount or a gift, but it is the merchant's call.
function isBelowCatalogPrice(
  article: ReturnRequestExchangeNewProductForExchangeCreditDto,
) {
  return (
    article.catalogPrice != null &&
    Math.round(article.price * 100) < Math.round(article.catalogPrice * 100)
  );
}

export function NewExchangeArticle({
  article,
  currency,
}: NewExchangeArticleProps) {
  const t = useTranslation();
  const isBelowCatalog = isBelowCatalogPrice(article);

  return (
    <div className="relative items-stretch h-28">
      <div className="absolute bg-transparent hover:bg-muted/40 rounded-lg flex gap-4 inset-2 p-2">
        <div>
          <img
            src={article.image ?? "https://placehold.co/600x400"}
            alt="exchange items"
            className="h-full aspect-[9/12] rounded-lg object-cover"
          />
        </div>
        <div className="flex-1 flex justify-between">
          <div className="flex flex-1 flex-col gap-1">
            <span className="font-semibold text-sm inline-flex items-center gap-2 flex-wrap">
              {article.title}
            </span>
            {article.sku && (
              <span className="text-xs text-muted-foreground">
                {article.sku}
              </span>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-md inline-flex items-baseline gap-1">
              <ArticlePrice
                price={
                  isBelowCatalog && article.catalogPrice != null
                    ? article.catalogPrice
                    : article.price
                }
                paidPrice={isBelowCatalog ? article.price : undefined}
                quantity={article.quantity}
                currency={currency}
              />
            </span>
            {isBelowCatalog && (
              <span
                title={t("returnDetail.exchangeCredit.belowCatalogPriceHint")}
              >
                <Badge
                  variant="outline"
                  className="border-transparent bg-amber-100 text-amber-700"
                >
                  {t("returnDetail.exchangeCredit.belowCatalogPrice")}
                </Badge>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
